import React from 'react';
import { SupportedSize, CommonProps, SupportedColor } from '../Element/Element';
import BodyText from '../Typography/BodyText/BodyText';
import Div from '../Element/Div/Div';
import Flex from '../Flex/Flex';
import Gutter from '../Gutter/Gutter';
import Icon from '../Icon/Icon';
import Link from '../Link/Link';
import MarginSpace from '../MarginSpace/MarginSpace';
import { SupportedIconName } from '../../shared/icons';
import styles from './GrowlNotification.module.scss';
import { ArrayElementType } from '../../types/utils/ArrayElementType';

type GrowlNotificationProps = {
  /** Notification message to be displayed */
  notification?: string;
  /** Notification style variant */
  variant?: GrowlNotificationSupportedVariant;
  /** Icon  */
  icon?: SupportedIconName;
  /** Background color */
  background?: SupportedColor;
  /** Vertical padding */
  verticalPadding?: SupportedSize;
  /** Positioning */
  position?: GrowlNotificationSupportedPositions;
  /** Call to Action Text  */
  ctaText?: string;
  /** Call to Action to / href. */
  ctaTo?: string;
  /** Call to Action onClick function, works with or without custom wrapper */
  ctaOnClick?: () => void;
  /** Custom wrapper for Call to Action */
  wrapper?: any;
  /** Custom wrapper props  */
  wrapperProps?: any;
  /** Close icon onClick function */
  closeOnClick?: () => void;
} & CommonProps<'div'>;

const GrowlNotification: React.FunctionComponent<GrowlNotificationProps> = ({
  notification,
  variant = 'success',
  icon,
  background,
  verticalPadding,
  position = 'fixed',
  closeOnClick,
  wrapper: Wrapper = Link,
  ctaText,
  ctaOnClick,
  ctaTo,
  wrapperProps = {},
  ...props
}) => {
  return (
    <Div
      backgroundColor={background ? background : mapVariant[variant].background}
      className={`growl-notification-test ${styles.growlNotification} ${
        position && styles.growlNotification
      }_${position && position}`}
      shown={!!notification}
      {...props}
    >
      <MarginSpace
        padding={{ vertical: verticalPadding ? verticalPadding : 'xs' }}
        noPaddingForSmallerThanMd
      >
        <Flex spaceBetween centeredVertically>
          <Flex flexItem occupyVerticalSpace centeredVertically>
            <Gutter size={'sm'} responsiveHidden={{ widerThan: 'sm' }} />
            <Icon
              name={icon ? icon : mapVariant[variant].icon}
              size="md"
              color={mapVariant[variant].contentColor}
              responsiveHidden={{ thinnerThan: 'sm' }}
            />
            <Gutter responsiveHidden={{ thinnerThan: 'sm' }} />
            <BodyText
              tagName="span"
              flexItem
              color={mapVariant[variant].contentColor}
              size="sm"
              fontWeight="medium"
              className={styles.growlNotification_notification}
              style={{ WebkitBoxOrient: 'vertical' }}
            >
              {notification}
            </BodyText>
            <Gutter size={'sm'} responsiveHidden={{ widerThan: 'xs' }} />
            <Gutter size={'md'} responsiveHidden={{ thinnerThan: 'sm' }} />
          </Flex>

          <Flex centeredVertically>
            {ctaText && (
              <Wrapper
                data-testid="wrapper"
                to={ctaTo}
                onClick={ctaOnClick}
                className={
                  wrapperProps && wrapperProps.className
                    ? wrapperProps.className
                    : [
                        styles.growlNotification_wrapper,
                        styles.growlNotification_readMore_hover,
                      ]
                }
                {...wrapperProps}
              >
                <BodyText
                  tagName="span"
                  color={mapVariant[variant].contentColor}
                  size="sm"
                  fontWeight="medium"
                  responsiveHidden={{ thinnerThan: 'sm' }}
                >
                  {ctaText}
                </BodyText>

                <Icon
                  responsiveHidden={{ widerThan: 'xs' }}
                  size={'md'}
                  className={styles.growlNotification_readMore}
                  name="more-horizontal"
                  color={mapVariant[variant].contentColor}
                />
              </Wrapper>
            )}

            {closeOnClick && (
              <>
                <Gutter size={'sm'} responsiveHidden={{ widerThan: 'xs' }} />
                <Gutter size={'md'} responsiveHidden={{ thinnerThan: 'sm' }} />
                <Icon
                  name="close"
                  size={'md'}
                  tabIndex={0}
                  clickableAppearance
                  className={styles.growlNotification_close}
                  color={mapVariant[variant].contentColor}
                  onClick={closeOnClick}
                />
              </>
            )}
            <Gutter size={'sm'} responsiveHidden={{ widerThan: 'md' }} />
          </Flex>
        </Flex>
      </MarginSpace>
    </Div>
  );
};

export const growlVariants = [
  'warning',
  'error',
  'success',
  'info',
  'other',
  'secondary',
] as const;

export type GrowlNotificationSupportedVariant = ArrayElementType<
  typeof growlVariants
>;

export const growlPositions = ['relative', 'fixed'] as const;

export type GrowlNotificationSupportedPositions = ArrayElementType<
  typeof growlPositions
>;

export const mapVariant = {
  warning: {
    background: 'orange400',
    icon: 'alert-filled',
    contentColor: 'orange800',
  },
  error: {
    background: 'red600',
    icon: 'error-filled',
    contentColor: 'white',
  },
  success: {
    background: 'green700',
    icon: 'check-circle-filled',
    contentColor: 'white',
  },
  info: {
    background: 'blue600',
    icon: 'info-filled',
    contentColor: 'white',
  },
  other: {
    background: 'purple500',
    icon: 'star-filled',
    contentColor: 'white',
  },
  secondary: {
    background: 'gray800',
    icon: 'error-filled',
    contentColor: 'white',
  },
} as const;

export default GrowlNotification;
